import { useQuery } from '@tanstack/react-query';
import { makeBookingManagerApi } from 'services/BookingManagerApi/BookingManagerApi';
import {
  IPurchaseOrderSearchItem,
  IPurchaseOrdersSearchFilter,
} from 'services/BookingManagerApi/types/PurchaseOrdersSearchResponse';

export const PURCHASE_ORDERS_SEARCH_QUERY_KEY = 'purchaseOrdersSearch';

export interface IPurchaseOrdersSearchListResponse {
  data: IPurchaseOrderSearchItem[];
  meta: {
    total: number;
    page: number;
    perPage: number;
  };
}

export const defaultFilters: IPurchaseOrdersSearchFilter = {
  bookingRef: '',
  bookingStatuses: [],
  serviceDateFrom: undefined,
  serviceDateTo: undefined,
  lastUpdatePurchaseOrderFrom: undefined,
  lastUpdatePurchaseOrderTo: undefined,
  destinationCountryCodes: [],
  includeCancelled: false,
  supplierType: undefined,
  supplierSearch: '',
  confirmationNumber: '',
  rateType: 'Static',
  dueDateFrom: undefined,
  dueDateTo: undefined,
  invoiceStatuses: undefined,
  variancePercentMin: undefined,
  variancePercentMax: undefined,
  percentFundsMin: undefined,
  percentFundsMax: undefined,
  percentPayedMin: undefined,
  percentPayedMax: undefined,
};

export const usePurchaseOrdersSearchQuery = (
  page: number,
  perPage: number,
  filters: IPurchaseOrdersSearchFilter = defaultFilters
) => {
  return useQuery<IPurchaseOrdersSearchListResponse>({
    staleTime: 0,
    gcTime: 0,
    queryKey: [
      PURCHASE_ORDERS_SEARCH_QUERY_KEY,
      page,
      perPage,
      filters.bookingRef,
      filters.bookingStatuses,
      filters.serviceDateFrom,
      filters.serviceDateTo,
      filters.lastUpdatePurchaseOrderFrom,
      filters.lastUpdatePurchaseOrderTo,
      filters.destinationCountryCodes,
      filters.includeCancelled,
      filters.supplierType,
      filters.supplierSearch,
      filters.confirmationNumber,
      filters.rateType,
      filters.dueDateFrom,
      filters.dueDateTo,
      filters.invoiceStatuses,
      filters.variancePercentMin,
      filters.variancePercentMax,
      filters.percentFundsMin,
      filters.percentFundsMax,
      filters.percentPayedMin,
      filters.percentPayedMax,
    ],
    queryFn: async () => {
      const api = makeBookingManagerApi();
      const offset = (page - 1) * perPage;

      const apiFilter: IPurchaseOrdersSearchFilter = {
        rateType: filters.rateType ?? 'Static',
        includeCancelled: filters.includeCancelled ?? false,
      };

      if (filters.bookingRef) {
        apiFilter.bookingRef = filters.bookingRef;
      }
      if (filters.bookingStatuses && filters.bookingStatuses.length > 0) {
        apiFilter.bookingStatuses = filters.bookingStatuses;
      }
      if (filters.serviceDateFrom) {
        apiFilter.serviceDateFrom = filters.serviceDateFrom;
      }
      if (filters.serviceDateTo) {
        apiFilter.serviceDateTo = filters.serviceDateTo;
      }
      if (filters.lastUpdatePurchaseOrderFrom) {
        apiFilter.lastUpdatePurchaseOrderFrom = filters.lastUpdatePurchaseOrderFrom;
      }
      if (filters.lastUpdatePurchaseOrderTo) {
        apiFilter.lastUpdatePurchaseOrderTo = filters.lastUpdatePurchaseOrderTo;
      }
      if (filters.destinationCountryCodes && filters.destinationCountryCodes.length > 0) {
        apiFilter.destinationCountryCodes = filters.destinationCountryCodes;
      }
      if (filters.supplierType) {
        apiFilter.supplierType = filters.supplierType;
      }
      if (filters.supplierSearch) {
        apiFilter.supplierSearch = filters.supplierSearch;
      }
      if (filters.confirmationNumber) {
        apiFilter.confirmationNumber = filters.confirmationNumber;
      }
      if (filters.dueDateFrom) {
        apiFilter.dueDateFrom = filters.dueDateFrom;
      }
      if (filters.dueDateTo) {
        apiFilter.dueDateTo = filters.dueDateTo;
      }
      if (filters.invoiceStatuses && filters.invoiceStatuses.length > 0) {
        apiFilter.invoiceStatuses = filters.invoiceStatuses;
      }
      if (filters.variancePercentMin !== undefined) {
        apiFilter.variancePercentMin = filters.variancePercentMin;
      }
      if (filters.variancePercentMax !== undefined) {
        apiFilter.variancePercentMax = filters.variancePercentMax;
      }
      if (filters.percentFundsMin !== undefined) {
        apiFilter.percentFundsMin = filters.percentFundsMin;
      }
      if (filters.percentFundsMax !== undefined) {
        apiFilter.percentFundsMax = filters.percentFundsMax;
      }
      if (filters.percentPayedMin !== undefined) {
        apiFilter.percentPayedMin = filters.percentPayedMin;
      }
      if (filters.percentPayedMax !== undefined) {
        apiFilter.percentPayedMax = filters.percentPayedMax;
      }

      const response = await api.searchPurchaseOrders({
        filter: apiFilter,
        pagination: { offset, limit: perPage },
      });

      return {
        data: response.data.items,
        meta: {
          total: response.data.count,
          page,
          perPage,
        },
      };
    },
  });
};
